import * as React from 'react';
import SurveyService from '../services/SurveyService';
import qs from 'query-string';
import Survey from '../components/Surveys/util/Survey';
import { selectActiveProjectId } from '../redux/modules/projectPicker/selectors';
import { useSelector } from 'react-redux';
import useServiceAuth from './useServiceAuth';
import useToggle from './useToggle';
import { useCustomCompareEffect } from 'react-use';
import { defineMessages, useIntl } from 'react-intl';
import { useNotify } from './useNotify';
import _isEqual from 'lodash/isEqual';
import _uniq from 'lodash/uniq';

const surveyService = new SurveyService();

export const messages = defineMessages({
  loadError: {
    id: 'useSurveys.loadError',
    defaultMessage: 'Error occurred loading surveys',
  },
});

export const useSurveys = (
  status?: string,
  includeInternal?: boolean,
  surveyIds?: string[],
) => {
  const serviceAuth = useServiceAuth();
  const [isLoading, setLoading, setLoadingComplete] = useToggle(false);
  const [surveys, setSurveys] = React.useState<fhir.Questionnaire[]>([]);
  const activeProjectId = useSelector(selectActiveProjectId);
  const [notify] = useNotify();
  const { formatMessage } = useIntl();
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const [error, setError] = React.useState<Error>(null);

  const initializeSurveyList = () => {
    if (isLoading || !activeProjectId || surveyIds?.length === 0) {
      return;
    }

    async function loadAllSurveys() {
      let nextPageToken = null;
      const rawSurveys: fhir.Questionnaire[] = [];
      do {
        const params: any = {
          datasetId: activeProjectId,
          status,
          pageSize: 10,
          id: _uniq(surveyIds)
            .map(id => id.split('/').pop())
            .join(','),
        };

        if (nextPageToken) {
          params.nextPageToken = nextPageToken;
        }

        const response = await surveyService.getSurveys(serviceAuth)(params);
        rawSurveys.push(...(response?.data?.items || []));
        nextPageToken = null;
        const nextLink = response.data?.links?.next;
        if (nextLink) {
          const queryParams = nextLink?.split('?')[1];
          // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
          nextPageToken = qs.parse(queryParams)?.nextPageToken || null;
        }
      } while (nextPageToken);

      const validSurveys = rawSurveys?.filter((item: fhir.Questionnaire) => {
        const survey = new Survey(item);
        return (includeInternal || !survey.isInternalSurvey) && !!survey.title;
      });
      validSurveys?.sort((q1: fhir.Questionnaire, q2: fhir.Questionnaire) =>
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        q1.title.localeCompare(q2.title),
      );

      return validSurveys;
    }

    setSurveys([]);
    setLoading();
    loadAllSurveys()
      .then(setSurveys)
      .catch(e => {
        setError(e);
        notify(formatMessage(messages.loadError), 'error');
      })
      .finally(setLoadingComplete);
  };

  useCustomCompareEffect(
    initializeSurveyList,
    [status, activeProjectId, surveyIds],
    (
      [prevStatus, prevProject, prevSurveyIds],
      [status, project, surveyIds],
    ) => {
      return (
        prevStatus === status &&
        prevProject === project &&
        _isEqual(prevSurveyIds, surveyIds)
      );
    },
  );

  return {
    isFetchingSurveys: isLoading,
    fetchSurveysError: error,
    resetSurveys: initializeSurveyList,
    surveys,
  };
};
